import React, { useState } from 'react';
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp, User, CreditCard, Monitor, Calculator, FileText, BarChart3, Wrench, BookOpen, Users, Shield, Rocket, Mail } from 'lucide-react';
import './FooterPages.css';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button className="faq-question" onClick={onToggle}>
        <span>{question}</span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {isOpen && (
        <div className="faq-answer">
          {answer}
        </div>
      )}
    </div>
  );
};

const FAQSection = ({ title, icon: Icon, faqs }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faq-section">
      <div className="faq-section-header">
        <Icon size={24} />
        <h2>{title}</h2>
      </div>
      <div className="faq-list">
        {faqs.map((faq, index) => (
          <FAQItem
            key={index}
            question={faq.question}
            answer={faq.answer}
            isOpen={openIndex === index}
            onToggle={() => toggleFAQ(index)}
          />
        ))}
      </div>
    </div>
  );
};

const FAQs = ({ onBack }) => {
  const faqSections = [
    {
      title: "General Questions",
      icon: HelpCircle,
      faqs: [
        {
          question: "1. What is Research Methodology Wizard?",
          answer: (
            <div>
              <p>Research Methodology Wizard is a comprehensive web application designed to guide researchers, students, and academics through the process of designing robust research methodologies. The app provides:</p>
              <ul>
                <li>Step-by-step wizards for defining research questions and selecting appropriate methodologies</li>
                <li>Tools for creating surveys, interviews, focus groups, and other data collection instruments</li>
                <li>A questionnaire builder similar to SurveyMonkey for distributing surveys</li>
                <li>Framework generators for theoretical, conceptual, analytical, and methodological frameworks</li>
                <li>Sample size calculators using multiple techniques (Cochran, Krejcie & Morgan, Slovin, etc.)</li>
                <li>Comprehensive final reports with export options (PDF, DOCX, PNG)</li>
              </ul>
            </div>
          )
        },
        {
          question: "2. Who is this app designed for?",
          answer: (
            <div>
              <p>Research Methodology Wizard is ideal for:</p>
              <ul>
                <li>Undergraduate students working on research projects</li>
                <li>Graduate students (Master's and PhD candidates) designing their thesis or dissertation research</li>
                <li>Academic researchers planning new studies</li>
                <li>Professional researchers in market research, UX research, or policy analysis</li>
                <li>Anyone needing structured guidance for research design</li>
              </ul>
            </div>
          )
        },
        {
          question: "3. Do I need prior research experience to use the app?",
          answer: (
            <div>
              <p>No! The app is designed to guide users at all levels:</p>
              <ul>
                <li><strong>Beginners:</strong> Step-by-step wizards with explanations and examples</li>
                <li><strong>Intermediate users:</strong> Quick access to tools with contextual tips</li>
                <li><strong>Advanced users:</strong> Customizable templates and advanced features</li>
              </ul>
              <p>Each step includes a glossary, tips, and best practices to help you understand research methodology concepts.</p>
            </div>
          )
        },
        {
          question: "4. Is Research Methodology Wizard free?",
          answer: (
            <div>
              <p>We offer a free tier that includes:</p>
              <ul>
                <li>Full access to Wizard A (Gap Finder)</li>
                <li>Limited access to Wizard B (Research Methodology - first 3 steps)</li>
                <li>Limited access to Wizard C (Data Analysis - first 3 steps)</li>
                <li>Limited access to Wizard D (Framework Builder - first 3 steps)</li>
                <li>Limited preview of Final Report (first 3 sections)</li>
              </ul>
              <p>Paid subscriptions unlock:</p>
              <ul>
                <li>Full Final Report (10+ sections)</li>
                <li>Framework Builder (4 framework types)</li>
                <li>All export formats (PDF, DOCX, PNG, JSON)</li>
                <li>Questionnaire Builder with unlimited surveys and responses</li>
                <li>Advanced features (R/SPSS code snippets, APA citations, etc.)</li>
                <li>Data Collection Wizard (all 6 paths)</li>
              </ul>
            </div>
          )
        },
        {
          question: "5. What are the subscription pricing options?",
          answer: (
            <div>
              <p>Research Methodology Wizard operates on a subscription-based model with the following pricing (subject to change):</p>
              <ul>
                <li><strong>Free Tier:</strong> No cost, very limited use - ideal to see what the app has to offer</li>
                <li><strong>Basic User:</strong> $2.00 USD per week</li>
                <li><strong>Pro User:</strong> $4.00 USD per month - ideal for BSc and MSc students</li>
                <li><strong>Advanced User:</strong> $6.00 USD per month - ideal for MSc, PhD students, postdoctoral and permanent researchers</li>
                <li><strong>Super User:</strong> $50.00 USD per year - 50% discount for annual subscription</li>
              </ul>
              <p>All prices are in USD and exclude applicable taxes.</p>
            </div>
          )
        }
      ]
    },
    {
      title: "Account and Subscription",
      icon: User,
      faqs: [
        {
          question: "6. How do I create an account?",
          answer: (
            <div>
              <ol>
                <li>Click "Sign Up" on the homepage</li>
                <li>Enter your email address and create a secure password</li>
                <li>Verify your email address (check your inbox for a confirmation link)</li>
                <li>Complete your profile (optional: name, institution)</li>
                <li>Start using Wizard A, Wizard B and Wizard C for free!</li>
              </ol>
            </div>
          )
        },
        {
          question: "7. Can I try the paid features before subscribing?",
          answer: (
            <div>
              <p>Yes! Free users can access:</p>
              <ul>
                <li>All of Wizard A, Wizard B, Wizard C and Wizard D for free (limited features)</li>
                <li>A 3-section preview of the Final Report</li>
              </ul>
              <p>This allows you to explore the app's value before committing to a subscription.</p>
            </div>
          )
        },
        {
          question: "8. How do I upgrade to a paid subscription?",
          answer: (
            <div>
              <ol>
                <li>Log in to your account</li>
                <li>Navigate to "Pricing" or click "Upgrade" when prompted</li>
                <li>Select your preferred subscription plan</li>
                <li>Enter your payment information (we accept all major credit/debit cards)</li>
                <li>Confirm your subscription</li>
                <li>Instant access to all premium features!</li>
              </ol>
            </div>
          )
        },
        {
          question: "9. Can I cancel my subscription?",
          answer: (
            <div>
              <p>Yes, you can cancel at any time:</p>
              <ol>
                <li>Go to Account Settings → Subscription</li>
                <li>Click "Cancel Auto-Renewal"</li>
                <li>Your subscription will remain active until the end of your current billing period</li>
                <li>After that, your account reverts to the free tier</li>
              </ol>
            </div>
          )
        },
        {
          question: "10. Are payments refundable?",
          answer: (
            <div>
              <p><strong>No. All payments are strictly non-refundable</strong>, except where required by law.</p>
              <p>Before subscribing, we encourage you to:</p>
              <ul>
                <li>Use the free tier to explore the app</li>
                <li>Review the 3-section preview of the Final Report</li>
                <li>Ensure the app meets your research needs</li>
              </ul>
            </div>
          )
        },
        {
          question: "11. Will my subscription automatically renew?",
          answer: (
            <p>Yes. All subscriptions automatically renew at the end of each billing cycle to ensure uninterrupted access. You can cancel auto-renewal at any time through your account settings.</p>
          )
        },
        {
          question: "12. What happens if my payment fails?",
          answer: (
            <div>
              <p>If a payment fails (e.g., expired card, insufficient funds):</p>
              <ul>
                <li>We'll send you an email notification</li>
                <li>You'll have 7 days to update your payment information</li>
                <li>If payment is not received within 7 days, your account may be suspended</li>
                <li>You'll still be able to log in and export your existing work, but premium features will be locked</li>
              </ul>
            </div>
          )
        }
      ]
    },
    {
      title: "Using the App",
      icon: Monitor,
      faqs: [
        {
          question: "13. What is 'Wizard A' Research Gap Finder?",
          answer: (
            <div>
              <p>Wizard A: Research Gap Finder is your starting point. It is a 7-phase evidence-based tool that helps you identify and validate gaps in existing research literature before you design your study. It analyses abstracts from 10–15 scholarly articles to detect six gap types (Theoretical, Empirical, Methodological, Contextual, Contradictory, and Practical), then scores their validity and generates a full 300+ word gap statement with citations. Its output feeds directly into Wizard B (Research Methodology).</p>
            </div>
          )
        },
        {
          question: "14. What is 'Wizard B' Research Methodology?",
          answer: (
            <div>
              <p>Wizard B: Research Methodology is an 8-step guided process that helps you:</p>
              <ul>
                <li>Define your research topic</li>
                <li>Formulate clear research questions</li>
                <li>Establish research objectives</li>
                <li>Align questions with objectives</li>
                <li>Identify variables (independent, dependent, moderating, mediating)</li>
                <li>Define your research scope</li>
                <li>Preview your research plan</li>
              </ul>
            </div>
          )
        },
        {
          question: "15. What is 'Wizard C' Data Analysis?",
          answer: (
            <div>
              <p>Wizard C: Data Analysis is a 7-step wizard that builds upon Wizard B and guides you through:</p>
              <ul>
                <li>Selecting your research philosophy (positivism, interpretivism, pragmatism, etc.)</li>
                <li>Choosing your research approach (deductive, inductive, abductive)</li>
                <li>Selecting your methodological choice (quantitative, qualitative, mixed methods)</li>
                <li>Choosing research strategies (survey, case study, experiment, ethnography, etc.)</li>
                <li>Determining your time horizon (cross-sectional vs. longitudinal)</li>
              </ul>
              <p>Wizard C implements the Research Onion framework by Saunders, Lewis, and Thornhill.</p>
            </div>
          )
        },
        {
          question: "15. What is 'Framework: Learn the Basics'?",
          answer: (
            <div>
              <p>This resource teaches you the fundamentals of creating a framework.</p>
              <p>Our "Framework Builder" helps you identify and map your main research constructs as either your dependent, independent, mediating, moderating and control variables.</p>
              <p>In addition we help you align the theories that work best for your research.</p>
              <p>The output is a report as well as a conceptual framework and methodological framework that you can download as high quality image (PNG) - for your methodology section/chapter.</p>
            </div>
          )
        },
        {
          question: "16. What is the Data Collection 'Wizard E'?",
          answer: (
            <div>
              <p>The Data Collection Wizard provides detailed guidance and tools for six data collection methods.</p>
              <p>We help you understand all the main data collection processes, with tips, best practices and examples so that you know exactly what data you need and in what format - and HOW to collect it efficiently and ethically.</p>
              <p>Knowing exactly what data to collect and how to collect it properly ensures methods align with aims, reduces error, improves reliability, and enables researchers to answer objectives with credible evidence.</p>
              <p>Each path includes 50-80 practical tips and examples.</p>
            </div>
          )
        },
        {
          question: "17. What is the Questionnaire Builder?",
          answer: (
            <div>
              <p>The Questionnaire Builder is a full-featured survey creation tool (similar to Google Forms or SurveyMonkey) that allows you to:</p>
              <ul>
                <li>Create surveys with 10 question types (multiple choice, checkboxes, dropdowns, linear scales, Likert scales, short/long text, yes/no, email, number)</li>
                <li>Drag-and-drop question reordering</li>
                <li>Mark questions as required</li>
                <li>Live preview your survey</li>
                <li>Share via link, QR code, or embed code</li>
                <li>Collect responses with anti-duplicate protection</li>
                <li>View analytics: summary stats, charts (pie charts, bar graphs), individual responses</li>
                <li>Export results to CSV or PDF</li>
              </ul>
            </div>
          )
        },
        {
          question: "18. What is the Framework Builder?",
          answer: (
            <div>
              <p>The Framework Builder automatically generates four types of research frameworks based on your Wizard B and Wizard C choices:</p>
              <ul>
                <li><strong>Theoretical Framework</strong> - Philosophical foundation, epistemology, key theories, propositions</li>
                <li><strong>Conceptual Framework</strong> - Constructs, variables, hypothesized relationships, operational definitions</li>
                <li><strong>Analytical Framework</strong> - Data analysis approach, statistical tests, validation methods</li>
                <li><strong>Methodological Framework</strong> - Research Onion layers (philosophy, approach, method, strategy, time horizon, techniques)</li>
              </ul>
              <p>Each framework includes structured text sections (editable), interactive diagrams, and export options (PDF, PNG, DOCX, JSON).</p>
            </div>
          )
        },
        {
          question: "19. What is 'Wizard F' Ethics Approval?",
          answer: (
            <div>
              <p>Our "Ethics Approval" wizard is a dry-run regardless of whether your institution has an Ethics committee or not.</p>
              <p>This is very invaluable and comes with a final "Ethics Report" customised for your research. Ethics self-assessment strengthens research validity and trustworthiness by ensuring transparency, protecting participants, minimising bias and harm, improving data integrity, and demonstrating accountability even when formal institutional ethical approval is not required.</p>
            </div>
          )
        },
        {
          question: "20. What is 'Wizard G' Survey Builder and Qualitative Analysis?",
          answer: (
            <div>
              <h4>Survey and Quantitative Analysis</h4>
              <p>Most people come up with survey questions from intuition or guesswork. However, our specially designed tool uses your previous decisions (Wizard A to Wizard F) to help you build professional survey questionnaires with multiple question types.</p>
              <p>You can generate a link to distribute your questionnaire online. We not only help you build the questionnaire, but we also assist you with descriptive statistics analysis, inferential statistical analysis, as well as thematic and sentiment analysis.</p>
              
              <h4>Qualitative Analysis</h4>
              <p>Even when you have collected qualitative data from other means (e.g. interview transcripts, focus group notes, or documents) you can upload them in various formats (TXT, DOCX, PDF, or Excel formats).</p>
              <p>Our Qualitative Analysis will interpret your non-numerical (text) data using 7 powerful methods: Thematic Analysis, Content Analysis, Sentiment Analysis, Narrative Analysis, Discourse Analysis, Grounded Theory, and Phenomenological Analysis.</p>
            </div>
          )
        },
        {
          question: "21. Can I edit my research plan after completing the wizards?",
          answer: (
            <div>
              <p>Yes! You can:</p>
              <ul>
                <li>Go back and edit any step in any Wizard at any time</li>
                <li>Your Final Report and frameworks update automatically based on your changes</li>
                <li>Save multiple versions of your research plan and compare any two versions side by side</li>
              </ul>
            </div>
          )
        },
        {
          question: "22. What export formats are available?",
          answer: (
            <div>
              <p>Paid subscribers can export:</p>
              <ul>
                <li><strong>Final Report:</strong> PDF (with table of contents, formatted sections) or DOCX (editable Word document)</li>
                <li><strong>Frameworks:</strong> PDF (text + diagram), PNG (diagram only), DOCX (text only), JSON (raw data)</li>
                <li><strong>Questionnaire Responses:</strong> CSV (for statistical analysis), PDF (formatted report)</li>
              </ul>
              <p>Free users can view and copy text from the 3-section preview but cannot export full reports.</p>
            </div>
          )
        },
        {
          question: "23. Is my research data saved automatically?",
          answer: (
            <div>
              <p>Yes! All your work is auto-saved as you progress through the wizards. You can:</p>
              <ul>
                <li>Log out and return anytime to continue where you left off</li>
                <li>Access your projects from any device with internet access</li>
                <li>Your data is securely stored in the cloud</li>
              </ul>
            </div>
          )
        },
        {
          question: "24. Can I work on multiple research projects?",
          answer: (
            <div>
              <p>Not currently. Each account supports one active research project at a time. If you need to start a new project:</p>
              <ul>
                <li>Save or Export your current project</li>
                <li>Reset or start a new project (this will overwrite your current data)</li>
              </ul>
            </div>
          )
        }
      ]
    },
    {
      title: "Sample Size Calculators",
      icon: Calculator,
      faqs: [
        {
          question: "25. What sample size calculation methods are available?",
          answer: (
            <div>
              <p>We provide multiple evidence-based techniques:</p>
              <ul>
                <li><strong>Cochran's Formula</strong> - For large populations (quantitative surveys)</li>
                <li><strong>Krejcie & Morgan Table</strong> - Quick lookup for known population sizes</li>
                <li><strong>Slovin's Formula</strong> - Simple calculation for finite populations</li>
                <li><strong>Yamane's Formula</strong> - Alternative for finite populations</li>
                <li><strong>Cochran's Adjustment for Small Populations</strong> - Adjusted version of Cochran's formula</li>
                <li><strong>Custom/Advanced</strong> - Input your own parameters</li>
              </ul>
              <p>Each calculator provides step-by-step formula explanations, assumptions and limitations, and recommended use cases.</p>
            </div>
          )
        },
        {
          question: "26. How accurate are the sample size calculators?",
          answer: (
            <div>
              <p>The sample size formulas are based on established statistical principles from recognized authorities (Cochran, Krejcie, Morgan, Slovin). However:</p>
              <p><strong>Important Disclaimer:</strong> Sample size determination depends on many factors specific to your study. The calculators provide general estimates and should be:</p>
              <ul>
                <li>Verified with a statistician or research supervisor</li>
                <li>Adjusted based on your specific research context</li>
                <li>Validated against disciplinary standards</li>
              </ul>
              <p><strong>NOTE:</strong> Research Methodology Wizard is not responsible for errors in sample size calculations or their application.</p>
            </div>
          )
        },
        {
          question: "27. Can I use these sample sizes for my thesis?",
          answer: (
            <div>
              <p>Yes, but always consult your research supervisor or institutional guidelines. Different disciplines and institutions may have specific requirements. Use our calculators as a starting point, then:</p>
              <ul>
                <li>Justify your sample size choice in your methodology section</li>
                <li>Reference the appropriate formula (Cochran, Krejcie & Morgan, etc.)</li>
                <li>Adjust based on practical constraints (time, budget, access)</li>
              </ul>
            </div>
          )
        }
      ]
    },
    {
      title: "Data Collection and Questionnaires",
      icon: FileText,
      faqs: [
        {
          question: "28. How many surveys can I create with the Questionnaire Builder?",
          answer: (
            <div>
              <ul>
                <li><strong>Free users:</strong> Cannot access the full Questionnaire Builder (limited preview only)</li>
                <li><strong>Paid subscribers:</strong> Unlimited surveys, unlimited questions per survey, unlimited responses</li>
              </ul>
            </div>
          )
        },
        {
          question: "29. Can I share my questionnaires with respondents?",
          answer: (
            <div>
              <p>Yes! Once you create a questionnaire, you can:</p>
              <ul>
                <li>Generate a shareable link (copy and paste into emails, social media, etc.)</li>
                <li>Download a QR code (print on flyers, posters, or display on screens)</li>
                <li>Get embed code (embed the survey on your website or blog)</li>
              </ul>
            </div>
          )
        },
        {
          question: "30. How do I collect responses?",
          answer: (
            <div>
              <p>Respondents access your survey via the shared link, QR code, or embedded form. The app:</p>
              <ul>
                <li>Validates responses (required fields, correct formats)</li>
                <li>Shows a progress indicator</li>
                <li>Prevents duplicate submissions (optional cookie/IP tracking)</li>
                <li>Displays a "Thank You" page after submission</li>
              </ul>
              <p>All responses are stored securely and accessible in your Results Dashboard.</p>
            </div>
          )
        },
        {
          question: "31. Can I see who responded to my survey?",
          answer: (
            <div>
              <p>Only if you explicitly ask for identifying information (e.g., email address, name fields). By default, responses are anonymous. You are responsible for:</p>
              <ul>
                <li>Informing respondents if you collect personal data</li>
                <li>Complying with privacy laws (GDPR, CCPA, etc.)</li>
                <li>Obtaining informed consent for research involving human subjects</li>
              </ul>
            </div>
          )
        },
        {
          question: "32. What analytics are provided for survey responses?",
          answer: (
            <div>
              <p>The Results Dashboard includes:</p>
              <ul>
                <li><strong>Summary statistics:</strong> Response count, completion rate, average time</li>
                <li><strong>Visualizations:</strong> Pie charts, bar graphs, word clouds</li>
                <li><strong>Individual responses:</strong> View each submission separately</li>
                <li><strong>Filtering and search:</strong> Find specific responses</li>
                <li><strong>Export:</strong> Download all data to CSV for analysis in Excel, SPSS, R, etc.</li>
              </ul>
            </div>
          )
        }
      ]
    },
    {
      title: "Frameworks and Reports",
      icon: BarChart3,
      faqs: [
        {
          question: "33. What is included in the Final Report?",
          answer: (
            <div>
              <p>The Final Report is a comprehensive research methodology document with 10+ sections:</p>
              <ul>
                <li>Research Title & Overview</li>
                <li>Research Questions & Objectives</li>
                <li>Research Philosophy</li>
                <li>Research Approach</li>
                <li>Methodological Choice (Quantitative/Qualitative/Mixed)</li>
                <li>Research Strategy</li>
                <li>Time Horizon</li>
                <li>Sampling Strategy & Sample Size Justification</li>
                <li>Data Collection Methods & Instruments</li>
                <li>Data Analysis Plan</li>
                <li>Ethical Considerations (checklist)</li>
                <li>Limitations & Delimitations</li>
                <li>References (APA format)</li>
              </ul>
              <p>Free users see only sections 1-3. Paid subscribers access the full report.</p>
            </div>
          )
        },
        {
          question: "34. Can I customize the frameworks?",
          answer: (
            <div>
              <p>Yes! For each of the four frameworks, you can:</p>
              <ul>
                <li>Edit text sections before exporting</li>
                <li>Add or remove constructs/variables</li>
                <li>Adjust relationships in the diagrams (drag and drop nodes)</li>
                <li>Change visual styles (colors, layouts)</li>
                <li>Toggle content sections on/off</li>
              </ul>
            </div>
          )
        },
        {
          question: "35. What diagram types are used in frameworks?",
          answer: (
            <div>
              <p>Each framework has a unique visualization:</p>
              <ul>
                <li><strong>Theoretical Framework:</strong> Hierarchical tree diagram (top-down structure)</li>
                <li><strong>Conceptual Framework:</strong> Network graph (force-directed layout showing relationships)</li>
                <li><strong>Analytical Framework:</strong> Flowchart (data flow from collection to analysis to interpretation)</li>
                <li><strong>Methodological Framework:</strong> Concentric circles (Research Onion model)</li>
              </ul>
              <p>Diagrams are interactive (drag nodes, zoom, pan) and exportable (PNG, PDF).</p>
            </div>
          )
        },
        {
          question: "36. Can I cite the frameworks in my thesis?",
          answer: (
            <div>
              <p>Yes! The frameworks are YOUR intellectual property. However, you should:</p>
              <ul>
                <li>Cite the underlying models (e.g., Saunders' Research Onion for the Methodological Framework)</li>
                <li>Acknowledge Research Methodology Wizard as the tool used to generate the diagrams (optional but recommended)</li>
              </ul>
              <p><strong>Example citation:</strong></p>
              <p><em>"The methodological framework was developed using Research Methodology Wizard (2026) based on Saunders, Lewis, and Thornhill's Research Onion model."</em></p>
            </div>
          )
        }
      ]
    },
    {
      title: "Technical and Support",
      icon: Wrench,
      faqs: [
        {
          question: "37. What browsers are supported?",
          answer: (
            <div>
              <p>Research Methodology Wizard works best on:</p>
              <ul>
                <li><strong>Desktop:</strong> Google Chrome, Mozilla Firefox, Safari, Microsoft Edge (latest versions)</li>
                <li><strong>Mobile:</strong> Safari (iOS), Chrome (Android)</li>
              </ul>
              <p>We recommend using a desktop or laptop for the best experience, especially when creating questionnaires and editing diagrams.</p>
            </div>
          )
        },
        {
          question: "38. Is the app mobile-friendly?",
          answer: (
            <div>
              <p>Yes! The app is fully responsive and works on:</p>
              <ul>
                <li>Smartphones (iOS, Android)</li>
                <li>Tablets (iPad, Android tablets)</li>
                <li>Laptops and desktops</li>
              </ul>
              <p>However, complex features like diagram editing and framework customization are easier on larger screens.</p>
            </div>
          )
        },
        {
          question: "39. Do I need to install any software?",
          answer: (
            <div>
              <p>No! Research Methodology Wizard is a web-based application. You only need:</p>
              <ul>
                <li>An internet connection</li>
                <li>A modern web browser</li>
                <li>An account (free to create)</li>
              </ul>
              <p>No downloads, installations, or plugins required.</p>
            </div>
          )
        },
        {
          question: "40. Is my data secure?",
          answer: (
            <div>
              <p>Yes. We take data security seriously:</p>
              <ul>
                <li><strong>Encryption in transit:</strong> All data is encrypted with TLS/SSL</li>
                <li><strong>Encryption at rest:</strong> Databases are encrypted</li>
                <li><strong>Secure payments:</strong> We use PCI-DSS compliant payment processors (Stripe)</li>
                <li><strong>Regular backups:</strong> Your data is backed up daily</li>
                <li><strong>Privacy controls:</strong> You control your data and can export or delete it at any time</li>
              </ul>
              <p>See our Privacy Policy page for full details.</p>
            </div>
          )
        },
        {
          question: "41. Can I download my data if I cancel my subscription?",
          answer: (
            <div>
              <p>Yes! Even if you cancel:</p>
              <ul>
                <li>You retain access to your data until the end of your billing period</li>
                <li>You can export all your work before cancellation</li>
                <li>After your subscription ends, you can still log in to view (but not export) your data for 90 days</li>
              </ul>
              <p>We recommend exporting your data before cancelling to avoid any loss.</p>
            </div>
          )
        },
        {
          question: "42. What happens if I close my account?",
          answer: (
            <div>
              <p>When you close your account:</p>
              <ul>
                <li>Your subscription is cancelled immediately (no refund)</li>
                <li>Your data is retained for 90 days (in case you change your mind)</li>
                <li>After 90 days, all your data is permanently deleted</li>
                <li>You cannot recover your data after deletion</li>
              </ul>
              <p>To reopen your account within 90 days, contact researchmethodologywizard@gmail.com.</p>
            </div>
          )
        },
        {
          question: "43. How do I get help or support?",
          answer: (
            <div>
              <p>We offer multiple support channels:</p>
              <ul>
                <li><strong>Email Support:</strong> researchmethodologywizard@gmail.com (response within 24-48 hours)</li>
                <li><strong>Help Center:</strong> Browse FAQs, tutorials, and guides</li>
                <li><strong>In-App Tips:</strong> Hover over (?) icons throughout the app for contextual help</li>
                <li><strong>Feature Requests:</strong> Suggest new features using our feedback form</li>
              </ul>
            </div>
          )
        }
      ]
    },
    {
      title: "Methodological Questions",
      icon: BookOpen,
      faqs: [
        {
          question: "44. I don't know which methodology to choose. Can the app decide for me?",
          answer: (
            <div>
              <p>The app guides you through methodology selection based on your research questions, but you make the final decision. Here's how it helps:</p>
              <ul>
                <li>Wizard B asks about your research goals (explore, explain, predict, etc.)</li>
                <li>Based on your answers, it suggests appropriate philosophies, approaches, and methods</li>
                <li>Each option includes explanations, examples, and when to use it</li>
                <li>You can change your selections and see how they affect your Final Report</li>
              </ul>
              <p><strong>Important:</strong> Always consult your research supervisor to ensure your methodology aligns with disciplinary norms and your research context.</p>
            </div>
          )
        },
        {
          question: "45. Can I use this app for qualitative research?",
          answer: (
            <div>
              <p>Absolutely! The app supports:</p>
              <ul>
                <li><strong>Qualitative methodologies:</strong> Interpretivism, constructivism, phenomenology</li>
                <li><strong>Qualitative strategies:</strong> Case studies, ethnography, grounded theory, narrative research</li>
                <li><strong>Qualitative data collection:</strong> Interviews (semi-structured, unstructured), focus groups, participant observation, document analysis</li>
                <li><strong>Qualitative analysis guidance:</strong> Thematic analysis, content analysis, discourse analysis, IPA</li>
              </ul>
            </div>
          )
        },
        {
          question: "48. Can I use this app for mixed methods research?",
          answer: (
            <div>
              <p>Yes! The app fully supports mixed methods designs:</p>
              <ul>
                <li>Select "Mixed Methods" in Wizard B</li>
                <li>Choose sequential, concurrent, or transformative mixed methods</li>
                <li>Design both quantitative (surveys) and qualitative (interviews, focus groups) instruments</li>
                <li>Get guidance on integration strategies (merging, connecting, embedding)</li>
                <li>Generate mixed methods frameworks and analysis plans</li>
              </ul>
            </div>
          )
        },
        {
          question: "49. Does the app provide statistical analysis tools?",
          answer: (
            <div>
              <p>No. Research Methodology Wizard helps you design your research and plan your analysis, but it does not perform statistical analysis. However, it does:</p>
              <ul>
                <li>Suggest appropriate statistical tests (t-tests, ANOVA, regression, etc.) based on your research questions and variables</li>
                <li>Provide code snippets for statistical software (R, SPSS, Python) to get you started</li>
                <li>Export your questionnaire responses to CSV for analysis in Excel, SPSS, R, etc.</li>
              </ul>
              <p>For actual statistical analysis, you'll need dedicated software like SPSS, R, Stata, or Python.</p>
            </div>
          )
        },
        {
          question: "50. Can I use this for systematic reviews or meta-analyses?",
          answer: (
            <div>
              <p>Not directly. Research Methodology Wizard is designed for primary research (collecting new data). For systematic reviews or meta-analyses, you'll need specialized tools like:</p>
              <ul>
                <li>Covidence or Rayyan for screening</li>
                <li>RevMan for meta-analysis</li>
                <li>PRISMA guidelines for reporting</li>
              </ul>
              <p>However, you can use our Document Analysis section for guidance on reviewing and synthesizing literature.</p>
            </div>
          )
        },
        {
          question: "51. How do I know if my sample size is adequate?",
          answer: (
            <div>
              <p>Sample size adequacy depends on:</p>
              <ul>
                <li><strong>Statistical power:</strong> Ability to detect an effect if it exists (typically 80% or higher)</li>
                <li><strong>Effect size:</strong> How large a difference or relationship you expect</li>
                <li><strong>Significance level:</strong> Usually α = 0.05</li>
                <li><strong>Research design:</strong> Between-subjects, within-subjects, multivariate, etc.</li>
              </ul>
              <p>Our calculators provide general estimates. For precise power analysis, use specialized software like G*Power (free) or consult a statistician.</p>
              <p><strong>Remember:</strong> Larger samples are generally better, but practical constraints may limit your sample size. Always justify your sample size in your methodology section.</p>
            </div>
          )
        }
      ]
    },
    {
      title: "Billing and Payments",
      icon: CreditCard,
      faqs: [
        {
          question: "52. What payment methods do you accept?",
          answer: (
            <div>
              <p>We accept:</p>
              <ul>
                <li>Visa, MasterCard, American Express, Discover</li>
                <li>Debit cards with Visa/MasterCard logos</li>
                <li>Digital wallets (Apple Pay, Google Pay) where available</li>
              </ul>
              <p>Payments are processed securely through Stripe, a PCI-DSS Level 1 certified payment processor.</p>
            </div>
          )
        },
        {
          question: "53. Is my payment information secure?",
          answer: (
            <div>
              <p>Yes. We never store your full credit card information on our servers. All payment data is:</p>
              <ul>
                <li>Processed directly by Stripe (PCI-DSS compliant)</li>
                <li>Encrypted end-to-end</li>
                <li>Protected by industry-leading security standards</li>
              </ul>
              <p>We only store a tokenized reference to your payment method and your billing address.</p>
            </div>
          )
        },
        {
          question: "54. Can I get an invoice or receipt?",
          answer: (
            <div>
              <p>Yes! After each payment, you'll receive:</p>
              <ul>
                <li>An email receipt sent to your registered email address</li>
                <li>An invoice available for download in your account under Billing History</li>
              </ul>
              <p>Invoices include: subscription tier, billing period, amount charged, payment date, and invoice number.</p>
            </div>
          )
        },
        {
          question: "55. Do you offer discounts for students or institutions?",
          answer: (
            <div>
              <p><strong>Student Discounts:</strong> Not currently, but our low-cost pricing ($2/month, $4/month) is designed to be accessible to students.</p>
              <p><strong>Institutional Licenses:</strong> We are currently developing Enterprise plans for universities and research institutions. Enterprise features will include:</p>
              <ul>
                <li>Multi-user licenses</li>
                <li>Centralized billing</li>
                <li>Admin dashboards</li>
                <li>Priority support</li>
                <li>Custom branding (optional)</li>
              </ul>
              <p>Interested? Contact enterprise@researchmethodologywizard.com.</p>
            </div>
          )
        },
        {
          question: "56. Can I upgrade or downgrade my subscription plan?",
          answer: (
            <div>
              <p><strong>Upgrading:</strong> Yes! You can upgrade to a longer-term plan at any time:</p>
              <ol>
                <li>Go to Account Settings → Subscription</li>
                <li>Select "Change Plan" and choose a longer-term option</li>
                <li>Pay the pro-rated difference</li>
                <li>Your new billing cycle starts immediately</li>
              </ol>
              <p><strong>Downgrading:</strong> You cannot downgrade mid-cycle. Instead, cancel auto-renewal, let your current subscription continue until it expires, then purchase a new, shorter-term plan if desired.</p>
            </div>
          )
        },
        {
          question: "57. What if I have a billing question or dispute?",
          answer: (
            <div>
              <p>Contact our billing support team:</p>
              <ul>
                <li><strong>Email:</strong> billing@researchmethodologywizard.com</li>
                <li><strong>Response time:</strong> 24-48 hours</li>
              </ul>
              <p>For disputes (incorrect charges, duplicate charges), we will investigate promptly and issue refunds if an error is confirmed.</p>
            </div>
          )
        }
      ]
    },
    {
      title: "Collaboration and Sharing",
      icon: Users,
      faqs: [
        {
          question: "58. Can I share my research plan with my supervisor or advisor?",
          answer: (
            <div>
              <p>Yes! You can:</p>
              <ul>
                <li>Export to PDF or DOCX and email the Final Report to your supervisor</li>
                <li>Share a read-only link (coming soon) that allows others to view your research plan without editing</li>
                <li>Print your Final Report for in-person meetings</li>
              </ul>
              <p><strong>Note:</strong> You cannot currently grant others direct editing access to your account. If you need collaborative features, let us know at researchmethodologywizard@gmail.com</p>
            </div>
          )
        },
        {
          question: "59. Can multiple people work on the same research project?",
          answer: (
            <div>
              <p>Not currently. Each account is for single-user access only. For collaborative research projects:</p>
              <ul>
                <li>Export your work at each stage and share with collaborators</li>
                <li>Use comments/track changes in Word for collaborative editing</li>
                <li>Check back for Team Plans (coming soon) which will support real-time collaboration</li>
              </ul>
            </div>
          )
        },
        {
          question: "60. Can I share my questionnaires on social media?",
          answer: (
            <div>
              <p>Yes! The Questionnaire Builder generates:</p>
              <ul>
                <li>Shareable links you can post on Facebook, Twitter, LinkedIn, etc.</li>
                <li>QR codes you can add to Instagram posts or print on flyers</li>
                <li>Embed codes for your website or blog</li>
              </ul>
              <p>Just copy and paste the link or code wherever you want to share your survey.</p>
            </div>
          )
        }
      ]
    },
    {
      title: "Ethics and Best Practices",
      icon: Shield,
      faqs: [
        {
          question: "61. Does the app provide ethics approval?",
          answer: (
            <div>
              <p><strong>No.</strong> Research Methodology Wizard cannot grant ethics approval. It provides:</p>
              <ul>
                <li>A checklist of common ethical considerations (informed consent, confidentiality, risk assessment, etc.)</li>
                <li>Guidance on ethical best practices</li>
                <li>Templates for consent forms (coming soon)</li>
              </ul>
              <p>However, you must:</p>
              <ul>
                <li>Submit your research plan to your Institutional Review Board (IRB) or Research Ethics Committee (REC)</li>
                <li>Obtain formal ethics approval before collecting data from human subjects</li>
                <li>Comply with all ethical regulations (Helsinki Declaration, Belmont Report, GDPR, etc.)</li>
              </ul>
            </div>
          )
        },
        {
          question: "62. Can I use this app for research involving human subjects?",
          answer: (
            <div>
              <p>Yes, but ethics approval is required:</p>
              <ol>
                <li>Complete your research design using Wizard A, Wizard B, and the Data Collection Wizard</li>
                <li>Export your Final Report and data collection instruments</li>
                <li>Submit your research protocol to your IRB/REC</li>
                <li>Await approval before recruiting participants or collecting data</li>
                <li>Follow all conditions and amendments specified by your ethics committee</li>
              </ol>
              <p>Research Methodology Wizard does not replace the ethics review process.</p>
            </div>
          )
        },
        {
          question: "63. How do I ensure my research is ethical?",
          answer: (
            <div>
              <p>Follow these principles:</p>
              <ul>
                <li><strong>Informed Consent:</strong> Explain your research to participants and obtain their voluntary agreement</li>
                <li><strong>Confidentiality:</strong> Protect participants' identities and data</li>
                <li><strong>Anonymity:</strong> Do not collect identifying information unless necessary</li>
                <li><strong>Minimize Harm:</strong> Avoid physical, psychological, or social risks</li>
                <li><strong>Transparency:</strong> Be honest about your research purpose and use of data</li>
                <li><strong>Right to Withdraw:</strong> Allow participants to withdraw at any time without penalty</li>
                <li><strong>Data Security:</strong> Store data securely and delete after the retention period</li>
              </ul>
              <p>Our app includes an Ethical Considerations Checklist in the Final Report to help you cover these bases.</p>
            </div>
          )
        },
        {
          question: "64. Can I use the app to collect sensitive data (health, financial, etc.)?",
          answer: (
            <div>
              <p>You can design instruments to collect sensitive data, but you must:</p>
              <ul>
                <li>Obtain ethics approval for research involving sensitive topics</li>
                <li>Use additional security measures (encryption, secure storage)</li>
                <li>Comply with data protection laws (GDPR, HIPAA, etc.)</li>
                <li>Inform participants about risks and how data will be protected</li>
              </ul>
              <p><strong>Note:</strong> Research Methodology Wizard stores all data securely, but it is your responsibility to ensure compliance with laws and ethical standards for sensitive data.</p>
            </div>
          )
        }
      ]
    },
    {
      title: "Future Features",
      icon: Rocket,
      faqs: [
        {
          question: "65. What features are coming next?",
          answer: (
            <div>
              <p><strong>Q2 2026:</strong></p>
              <ul>
                <li>Team/collaborative plans (multi-user accounts)</li>
                <li>Version control (save multiple drafts)</li>
                <li>Advanced statistical power analysis tools</li>
                <li>More export options (LaTeX, Markdown)</li>
              </ul>
              <p><strong>Q3 2026:</strong></p>
              <ul>
                <li>Consent form templates</li>
                <li>Gantt chart generator for research timelines</li>
                <li>Integration with reference managers (Zotero, Mendeley)</li>
                <li>Mobile app (iOS, Android)</li>
              </ul>
              <p><strong>Q4 2026:</strong></p>
              <ul>
                <li>AI-powered methodology suggestions</li>
                <li>Automated literature review summaries</li>
                <li>Integration with statistical software (SPSS, R, Stata)</li>
                <li>Institutional/enterprise dashboards</li>
                <li>Custom branding for organizations</li>
                <li>API access for researchers</li>
              </ul>
              <p>Want to suggest a feature? Email us at feedback@researchmethodologywizard.com!</p>
            </div>
          )
        },
        {
          question: "66. Can you integrate with my university's research management system?",
          answer: (
            <p>We're working on API integrations for institutional licenses. If your university is interested in integrating Research Methodology Wizard with existing systems (e.g., thesis submission portals, research management systems), contact us at enterprise@researchmethodologywizard.com.</p>
          )
        }
      ]
    }
  ];

  return (
    <div className="footer-page">
      <button className="back-button" onClick={onBack}>
        <ArrowLeft size={20} />
        Back
      </button>
      <div className="footer-page-content faq-page">
        <div className="faq-header">
          <HelpCircle size={40} className="faq-icon" />
          <h1>Frequently Asked Questions</h1>
          <p className="faq-subtitle">Find answers to common questions about Research Methodology Wizard</p>
        </div>

        {faqSections.map((section, index) => (
          <FAQSection
            key={index}
            title={section.title}
            icon={section.icon}
            faqs={section.faqs}
          />
        ))}

        <section className="faq-contact-section">
          <div className="section-header-icon">
            <Mail size={24} />
            <h2>Have More Questions?</h2>
          </div>
          <p>Contact us anytime:</p>
          <div className="contact-info">
            <p><strong>General Inquiries (including support, billing and partnerships):</strong><br />
              <a href="mailto:researchmethodologywizard@gmail.com">researchmethodologywizard@gmail.com</a>
            </p>
          </div>
          <div className="social-links">
            <p><strong>Follow us:</strong></p>
            <ul>
              <li>Website: www.researchmethodologywizard.com</li>
              <li>Twitter/X: @ResearchWizardApp</li>
              <li>LinkedIn: Research Methodology Wizard</li>
              <li>YouTube: Research Methodology Wizard (tutorials coming soon!)</li>
            </ul>
          </div>
          <p className="thank-you">Thank you for choosing Research Methodology Wizard. We're here to support your research journey!</p>
        </section>
      </div>
    </div>
  );
};

export default FAQs;
